import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArtData } from "../../../types";
import { getAltText } from "../utils/art";
import { CornerActionType, ImagePreview } from "./ImagePreview";
import { Price } from "./Price";
import { ImagePreviewV2 } from "./ImagePreviewV2";
import { makePriceString } from "../../../shared-utils/noDep/price";

type ArtConfigV2 =
  | {
      href?: string;
      showPrice?: "above" | "below";
      text?: string | JSX.Element;
      // onMouseEnter?: () => void;
      // onMouseLeave?: () => void;
      actionBelow?: {
        actionComponent: JSX.Element;
      };
      actionCorner?: CornerActionType;
      tileClassName?: string;
      height?: number;
      hoverHeight?: number; // defaults to height if undefined or missing
      filter?: string; //ex:"blur(10px)";
      hoverFilter?: string; // defaults to filter if undefined or missing
      overlayText?: string;
      hoverOverlayText?: string; // defaults to overlayText if undefined or missing
      opacity?: number;
      hoverOpacity?: number;
    }
  | undefined;
const ArtItemV2 = ({
  art,
  artConfig,
  preload,
  onTouchStartCapture,
  onMouseEnter,
  onMouseLeave,
  onViewEnter,
  onViewLeave,
  isFocus,
}: {
  art: ArtData;
  artConfig: ArtConfigV2;
  preload: boolean;
  onTouchStartCapture?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onViewEnter?: () => void;
  onViewLeave?: () => void;
  isFocus?: boolean;
}) => {
  const opacity = artConfig?.opacity;
  const hoverOpacity = artConfig?.hoverOpacity ?? artConfig?.opacity;
  const filter = artConfig?.filter;
  const hoverFilter = artConfig?.hoverFilter ?? artConfig?.filter;
  const height = artConfig?.height ?? 400;
  const hoverHeight =
    window?.innerWidth > 700
      ? (artConfig?.hoverHeight ?? artConfig?.height ?? 420)
      : height;
  const overlayText = artConfig?.overlayText;
  const hoverOverlayText =
    artConfig?.hoverOverlayText ?? artConfig?.overlayText;
  const { size, hoverSize } = useMemo(() => {
    return {
      size: {
        maxWidth: "80vw",
        height: height,
        top: 0,
        left:
          (height / hoverHeight - (hoverHeight - height) / hoverHeight / 2) *
            -100 +
          "%",
        transformX:
          (height / hoverHeight - (hoverHeight - height) / hoverHeight / 2) *
            100 +
          "%",
        transformY: 0,
        z: 1,
        filter: filter,
        text: overlayText,
        opacity: opacity,
      },
      hoverSize: {
        maxWidth: "80vw",
        height: hoverHeight,
        z: 100,
        top: height - hoverHeight,
        left: "-100%",
        transformX:
          (height / hoverHeight - (hoverHeight - height) / hoverHeight / 2) *
            100 +
          "%",
        transformY: 0,
        // imagePath:
        //   art.images && art.images.length > 1
        //     ? art.images[1].path
        //     : undefined,
        filter: hoverFilter,
        text: hoverOverlayText,
        opacity: hoverOpacity,
      },
    };
  }, [hoverHeight]);
  const Container = useCallback(
    (props: JSX.IntrinsicElements["a"]) =>
      artConfig?.href ? (
        <>
          <a {...props} />
        </>
      ) : (
        <>
          <div {...(props as JSX.IntrinsicElements["div"])} />
        </>
      ),
    [artConfig?.href],
  );
  return (
    <div
      key={art.slug}
      // Old wide card style
      // className="flex-[1_1_auto] lg:flex-[0.5_1_auto] duration-300"
      className="flex-[0_1_auto] lg:flex-[0_1_auto] duration-300 p-10 m-5"
      title={
        art.title +
        (art.price && art.currency
          ? " - " +
            makePriceString({ amount: art.price, currency: art.currency })
          : "")
      }
      onClick={(e) => {
        if (!artConfig?.href) {
          e.stopPropagation();
          e.preventDefault();
        }
      }}
    >
      <div
        className={
          // Old wide card style
          // (artConfig?.tileClassName ??
          //   " bg-secondary/70 backdrop-blur-lg text-textSecondary rounded-2xl border-solid border-2 border-textPrimary shadow-md hover:shadow-lg") +
          // " pt-7 px-4 pb-4 duration-300 "
          (artConfig?.tileClassName ?? " text-textSecondary") +
          " min-w-[30vw] duration-300 "
        }
      >
        {artConfig?.showPrice === "above" ? (
          <div className={art.orderSlug ? "line-through" : ""}>
            <Price item={art} />
          </div>
        ) : null}
        <Container
          href={artConfig?.href}
          className={
            "flex-1 items-center rounded-xl decoration-none flex justify-between flex flex-col" +
            (artConfig?.href ? " cursor-pointer " : " cursor-default ")
          }
          style={{
            textTransform: "capitalize",
            fontSize: "2rem",
          }}
        >
          <ImagePreviewV2
            preload={preload}
            useImageSize="size-h500"
            {...artConfig?.actionCorner}
            transition={300}
            absolute
            isFocus={isFocus}
            onViewEnter={onViewEnter}
            onViewLeave={onViewLeave}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onTouchStartCapture={onTouchStartCapture}
            // onMouseEnter={artConfig?.onMouseEnter}
            // onMouseLeave={artConfig?.onMouseLeave}
            alt={getAltText(art)}
            image={art.images?.[0]}
            size={size}
            hoverSize={hoverSize}
          />
          {/* <span style={{ flex: "0 0 auto" }}>{art.title}</span> */}
        </Container>
        {artConfig?.showPrice === "below" ||
        artConfig?.text ||
        artConfig?.actionBelow ? (
          <div className="pt-1 ">
            {artConfig?.text ? <div>{artConfig.text}</div> : null}

            {artConfig?.showPrice === "below" ? (
              <div
                className={"text-xs " + (art.orderSlug ? "line-through" : "")}
              >
                <Price item={art} />
              </div>
            ) : null}

            {artConfig?.actionBelow?.actionComponent ?? null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

type ArtListItem<MType> = ArtData & { __meta?: MType };
export const ArtListV2 = <MetaType,>({
  items,
  getArtConfig,
  preloadCount,
  justify = "justify-center",
  ...props
}: {
  items: ArtListItem<MetaType>[];
  noArt?: JSX.Element;
  getArtConfig?: (art: ArtData, __meta?: MetaType) => ArtConfigV2;
  preloadCount?: number;
  justify?: "justify-center" | "";
}) => {
  const [inView, setInView] = useState<Record<string, number | undefined>>({});
  const handleViewEnter = (slug: string) => {
    setInView((curr) => ({ ...curr, [slug]: new Date().getTime() }));
  };
  const handleViewLeave = (slug: string) => {
    setInView((curr) => ({ ...curr, [slug]: undefined }));
  };
  const checkIfWide = useCallback(() => {
    return window?.innerWidth > 700;
  }, []);
  const [touchEventsSeen, setTouchEventsSeen] = useState(false);

  const [inHover, setInHover] = useState<Record<string, boolean>>({});
  const handleMouseEnter = (slug: string) => {
    setInHover((curr) => ({ ...curr, [slug]: true }));
  };
  const handleMouseLeave = (slug: string) => {
    setInHover((curr) => ({ ...curr, [slug]: false }));
  };
  const isItFocused = (slug: string) => {
    const isWide = checkIfWide();
    if (!touchEventsSeen && inHover?.[slug]) {
      return true;
    }
    if (!isWide) {
      const viewsInOrder = Object.entries(inView)
        .filter(([, start]) => start)
        .sort((a, b) => (a[1] as number) - (b[1] as number));
      const mainView = viewsInOrder[Math.floor((viewsInOrder.length - 1) / 2)];
      if (!mainView) {
        return false;
      }
      if (viewsInOrder.length === 2) {
        if (viewsInOrder[1][0] === slug) {
          return true;
        } else {
          return false;
        }
      }
      if (mainView[0] === slug) {
        return true;
      }
    }
  };
  return (
    <div
      // Old wide card style
      // className="flex flex-wrap md:justify-center justify-between sm:mx-5 m-2 lg:mx-[5vw] 2xl:mx-[150px] gap-2"
      className={"flex flex-wrap " + justify}
    >
      {items?.length ? (
        items.map(({ __meta, ...art }, i) => (
          <ArtItemV2
            isFocus={isItFocused(art.slug)}
            onTouchStartCapture={() => {
              setTouchEventsSeen(true);
            }}
            onViewEnter={() => {
              handleViewEnter(art.slug);
            }}
            onViewLeave={() => {
              handleViewLeave(art.slug);
            }}
            onMouseEnter={() => {
              handleMouseEnter(art.slug);
            }}
            onMouseLeave={() => {
              handleMouseLeave(art.slug);
            }}
            preload={i < (preloadCount ?? 0)}
            art={art}
            artConfig={getArtConfig?.(art, __meta)}
            key={art.slug}
          />
        ))
      ) : props.noArt ? (
        props.noArt
      ) : (
        <>NO ART</>
      )}
    </div>
  );
};
